import { List } from 'immutable';
import _ from 'lodash';
import { assistanceReasons } from './constants';
import { booleanToYesNo, yesNoToBoolean } from '../../../utils/text';
import PassengerAssistActions from '../../../actions/PassengerAssistActions';
import UserStore from '../../../stores/UserStore';
import { isImmutable } from '../../../helpers/Immutable';
import PassengerAssistStore from '../../../stores/PassengerAssistStore';

export const areJourneysIdentical = (journey1, journey2) => {
    const isMatchingLegsTrainUids = _.isEqual(
        journey1.legs.map((leg) => leg.trainUid || '').sort(),
        journey2.legs.map((leg) => leg.trainUid || '').sort()
    );

    return journey1 && journey2 &&
        journey1.origin && journey2.origin &&
        journey1.origin.nlc === journey2.origin.nlc &&
        journey1.destination && journey2.destination &&
        journey1.destination.nlc === journey2.destination.nlc &&
        journey1.departureHour === journey2.departureHour &&
        journey1.arrivalHour === journey2.arrivalHour &&
        journey1.arrivalMinutes === journey2.arrivalMinutes &&
        isMatchingLegsTrainUids;
};

export const normalizeBookPAJourney = async journey => {
    const parsedJourney = isImmutable(journey) ? journey.toJS() : journey;

    return await Promise.all(
        parsedJourney.legs.map(async leg => {
            const { nlc: departureNLS } = leg.origin || leg.departure;
            const { nlc: arrivalNLS } = leg.destination || leg.arrival;

            const departure = await PassengerAssistActions.getAssistedTravelLocation(departureNLS);
            const arrival = await PassengerAssistActions.getAssistedTravelLocation(arrivalNLS);

            return {
                departure,
                arrival,
                departureDate: leg.departureDate || '',
                departureHour: leg.departureHour || '',
                departureMinutes: leg.departureMinutes || '',
                arrivalDate: leg.arrivalDate || '',
                arrivalHour: leg.arrivalHour || '',
                arrivalMinutes: leg.arrivalMinutes || '',
                trainUid: leg.trainUid || '',
                changeAt: null,
            };
        }),
    );
};

export const bookPAJourneyDetails = async travelAssist => {
    let outwardJourney = travelAssist.hasOutwardJourney()
        ? await normalizeBookPAJourney(travelAssist.outwardJourney)
        : [];

    let returnJourney = travelAssist.hasReturnJourney()
        ? await normalizeBookPAJourney(travelAssist.returnJourney)
        : [];

    if (!outwardJourney.length && returnJourney.length) {
        outwardJourney = returnJourney;
        returnJourney = [];
    }

    return { outwardJourney, returnJourney };
};

export const getBookPAAccessibilityProfile = ({ accessibilityProfileForm }) => {
    const { email } = UserStore.getCurrent();

    const {
        title,
        firstName,
        surname: lastName,
        line1: addressLine1,
        line2: addressLine2,
        line3: addressLine3,
        town,
        country: county,
        postCode,
        phone: contactNumber,
        contactMethod,
        assistanceLocation,
        otherAssistanceLocation,
        reasonForAssistance,
        otherAssistanceType,
        participateSurvey,
        participateImprovementSurvey,
        pushNotify,
    } = isImmutable(accessibilityProfileForm) ? accessibilityProfileForm.toJS() : accessibilityProfileForm;

    return {
        personalDetails: {
            title,
            firstName,
            lastName,
            addressLine1,
            addressLine2,
            addressLine3,
            town,
            county,
            postCode,
            contactNumber,
            email,
        },
        caseDetails: {
            contactMethod,
            assistanceLocation,
            otherAssistanceLocation,
            reasonForAssistance,
            otherAssistanceType: reasonForAssistance.includes(assistanceReasons.OTHER.value) ? otherAssistanceType : '',
            participateSurvey: booleanToYesNo(participateSurvey).toLowerCase(),
            participateImprovementSurvey: booleanToYesNo(participateImprovementSurvey).toLowerCase(),
            pushNotify,
        },
    };
};

export const getBookPassengerAssistRequestBody = async (payload = {}) => {
    const travelAssist = payload.travelAssist || PassengerAssistStore.get('travelAssist');

    const accessibilityProfileForm = PassengerAssistStore.get('accessibilityProfileForm');

    const journeyDetails = await bookPAJourneyDetails(travelAssist);

    const { personalDetails, caseDetails } = getBookPAAccessibilityProfile({ accessibilityProfileForm });
    const { pushNotify } = caseDetails;

    return {
        personalDetails,
        caseDetails,
        journeyDetails: {
            ...journeyDetails,
            pushNotify,
            orderId: payload.orderId,
        },
    };
};

export const getEditPassengerAssistRequestBody = async () => {
    const travelAssist = PassengerAssistStore.get('travelAssist');
    const accessibilityProfileForm = PassengerAssistStore.get('accessibilityProfileForm');
    const {
        number,
        sysId,
        customerId,
        link,
        journeyDetails,
        caseDetails: {
            meetingPoint,
            bookTicket,
            ticketReference
        }
    } = PassengerAssistStore.get('assistedBooking');

    const { outwardJourney, returnJourney } = await bookPAJourneyDetails(travelAssist);
    const {
        personalDetails,
        caseDetails: {
            contactMethod,
            assistanceLocation,
            otherAssistanceLocation,
            reasonForAssistance,
            otherAssistanceType,
            participateSurvey,
            participateImprovementSurvey,
            pushNotify,
        }
    } = getBookPAAccessibilityProfile({ accessibilityProfileForm });

    return {
        personalDetails,
        caseDetails: {
            contactMethod,
            assistanceLocation,
            otherAssistanceLocation,
            reasonForAssistance,
            otherAssistanceType,
            participateSurvey,
            participateImprovementSurvey,
            meetingPoint,
            bookTicket,
            ticketReference,
            pushNotify,
        },
        journeyDetails: {
            ...journeyDetails,
            outwardJourney,
            returnJourney,
            pushNotify,
        },
        customerId,
        number,
        sysId,
        link,
    };
};

export const normalizeTime = (time) => time ? time.toString().padStart(2, '0') : null;

export const getHourMinutes = (hour, minutes) => hour && minutes ? `${hour}:${minutes}` : null;

export const normalizeNLC = (number) => Math.floor(number / 100).toString();

export const normalizeJourneyDetails = (journey) => {
    if (!(journey && journey.length)) {
        return null;
    }
    const firstJourney = journey[0];
    const lastJourney = journey[journey.length - 1];
    const firstJourneyDeparture = firstJourney.departure;
    const lastJourneyArrival = lastJourney.arrival;

    const {
        departureDate,
        departureHour,
        departureMinutes,
    } = firstJourney;

    const {
        arrivalDate,
        arrivalHour,
        arrivalMinutes
    } = lastJourney;

    return {
        origin: {
            name: firstJourneyDeparture.name,
            nlc: normalizeNLC(firstJourneyDeparture.nlc),
            threeLetterCode: firstJourneyDeparture.threeLetterCode,
            sysId: firstJourneyDeparture.sysId,
        },
        destination: {
            name: lastJourneyArrival.name,
            nlc: normalizeNLC(lastJourneyArrival.nlc),
            threeLetterCode: lastJourneyArrival.threeLetterCode,
            sysId: lastJourneyArrival.sysId,
        },
        departureDate,
        departureHour: normalizeTime(departureHour),
        departureMinutes: normalizeTime(departureMinutes),
        arrivalDate,
        arrivalHour: normalizeTime(arrivalHour),
        arrivalMinutes: normalizeTime(arrivalMinutes),
        legs: new List(journey.map((leg) => ({
            ...leg,
            departureHour: normalizeTime(leg.departureHour),
            departureMinutes: normalizeTime(leg.departureMinutes),
            arrivalHour: normalizeTime(leg.arrivalHour),
            arrivalMinutes: normalizeTime(leg.arrivalMinutes),
        }))),
    };
};

export const normalizeAccessibilityProfile = ({ personalDetails, caseDetails, journeyDetails }) => {
    const {
        title,
        firstName,
        lastName: surname,
        contactNumber: phone,
        postCode,
        addressLine1: line1,
        addressLine2: line2,
        addressLine3: line3,
        town,
        county: country
    } = personalDetails;

    const {
        assistanceLocation,
        otherAssistanceLocation,
        reasonForAssistance,
        otherAssistanceType,
        participateSurvey,
        participateImprovementSurvey,
        contactMethod,
    } = caseDetails;

    const {
        pushNotify
    } = journeyDetails;

    return {
        title,
        firstName,
        surname,
        phone,
        postCode,
        line1,
        line2,
        line3,
        town,
        country,
        contactMethod,
        assistanceLocation,
        otherAssistanceLocation,
        reasonForAssistance: new List(reasonForAssistance),
        otherAssistanceType,
        participateSurvey: yesNoToBoolean(participateSurvey),
        participateImprovementSurvey: yesNoToBoolean(participateImprovementSurvey),
        pushNotify,
        id: null,
    };
};

export const normalizeAssistedBooking = (assistedBooking) => {
    if (_.isEmpty(assistedBooking)) {
        return {};
    }

    try {
        const {
            sysId,
            number,
            journeyDetails: {
                outwardJourney,
                returnJourney,
                returnJourneyState,
                outwardJourneyState
            },
        } = assistedBooking;

        return {
            outwardJourney: normalizeJourneyDetails(outwardJourney),
            returnJourney: normalizeJourneyDetails(returnJourney),
            accessibilityProfile: normalizeAccessibilityProfile(assistedBooking),
            sysId,
            status: returnJourney.length ? returnJourneyState : outwardJourneyState,
            number,
            legs: new List(),
        };
    } catch (error) {
        console.error('normalizeAssistedBooking error: ', error);
        return {};
    }
};

export const normalizeJourney = (journeyRecord) => {
    let journeyDepartureDateTime;
    let journeyArrivalDateTime;
    let journeyOriginStation;
    let journeyDestinationStation;

    try {
        journeyDepartureDateTime = journeyRecord.getDepartureDateTime();
        journeyArrivalDateTime = journeyRecord.getArrivalDateTime();
        journeyOriginStation = journeyRecord.getOriginStation();
        journeyDestinationStation = journeyRecord.getDestinationStation();

        return {
            origin: {
                name: journeyOriginStation.getName(),
                nlc: journeyOriginStation.get('nlc'),
            },
            destination: {
                name: journeyDestinationStation.getName(),
                nlc: journeyDestinationStation.get('nlc'),
            },
            changeAt: null,
            departureDate: journeyDepartureDateTime.format('DD/MM/YYYY'),
            departureHour: journeyDepartureDateTime.format('HH'),
            departureMinutes: journeyDepartureDateTime.format('mm'),
            arrivalDate: journeyArrivalDateTime.format('DD/MM/YYYY'),
            arrivalHour: journeyArrivalDateTime.format('HH'),
            arrivalMinutes: journeyArrivalDateTime.format('mm'),
            hasMetroLeg: !!journeyRecord.get('legs').find((leg) => leg.getIsMetro()),
            legs: journeyRecord.get('legs').map((leg) => {
                let departureTime;
                let arrivalTime;
                let originStation;
                let destinationStation;

                departureTime = leg.getDepartureTime();
                arrivalTime = leg.getArrivalTime();
                originStation = leg.getOriginStation();
                destinationStation = leg.getDestinationStation();

                return {
                    trainUid: leg.getTrainUid(),
                    origin: {
                        nlc: originStation.get('nlc'),
                        name: originStation.getName()
                    },
                    destination: {
                        nlc: destinationStation.get('nlc'),
                        name: destinationStation.getName()
                    },
                    departureHour: departureTime && departureTime.format('HH'),
                    departureMinutes: departureTime && departureTime.format('mm'),
                    departureDate: departureTime && departureTime.format('DD/MM/YYYY'),
                    arrivalHour: arrivalTime && arrivalTime.format('HH'),
                    arrivalMinutes: arrivalTime && arrivalTime.format('mm'),
                    arrivalDate: arrivalTime && arrivalTime.format('DD/MM/YYYY'),
                };
            }).filter(obj => obj.trainUid)
        };
    } catch (error) {
        console.error('normalizeJourney error: ', error);
        return error;
    }
};

export const getPAModalJourneyData = (journey) => {
    try {
        return {
            origin: journey.getOriginStation(),
            destination: journey.getDestinationStation(),
            departureTime: journey.getDepartureDateTime(),
            arrivalTime: journey.getArrivalDateTime(),
        };
    } catch (error) {
        console.error('getPAModalJourneyData error: ', error);
        return {
            origin: {},
            destination: {},
            departureTime: null,
            arrivalTime: null,
        };
    }
};

